/**
 * Reusable Feedback Form Component
 * Allows users to submit feature requests, bug reports, and other feedback
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { useTranslation } from '../../i18n';
import {
  Sparkles,
  Bug,
  Lightbulb,
  HelpCircle,
  MessageSquare,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { cn } from '../ui/utils';

export type FeedbackType = 'FEATURE_REQUEST' | 'BUG_REPORT' | 'IMPROVEMENT' | 'QUESTION' | 'OTHER';
export type FeedbackPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface FeedbackFormData {
  title: string;
  description: string;
  feedback_type: FeedbackType;
  priority: FeedbackPriority;
  component?: string;
}

interface FeedbackFormProps {
  /** Called when form is submitted. Should return a promise. */
  onSubmit: (data: FeedbackFormData) => Promise<void>;
  /** Optional default component name to pre-fill */
  defaultComponent?: string;
  /** Optional custom title */
  title?: string;
  /** Optional custom description */
  description?: string;
  /** Compact mode for embedding in other components */
  compact?: boolean;
  /** Hide card wrapper */
  noCard?: boolean;
}

const FEEDBACK_TYPES = [
  { value: 'FEATURE_REQUEST', label: 'Feature Request', icon: Sparkles, color: 'text-fuchsia-500' },
  { value: 'IMPROVEMENT', label: 'Improvement', icon: Lightbulb, color: 'text-amber-500' },
  { value: 'BUG_REPORT', label: 'Bug Report', icon: Bug, color: 'text-red-500' },
  { value: 'QUESTION', label: 'Question', icon: HelpCircle, color: 'text-blue-500' },
  { value: 'OTHER', label: 'Other', icon: MessageSquare, color: 'text-gray-500' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  { value: 'MEDIUM', label: 'Medium', color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'HIGH', label: 'High', color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'CRITICAL', label: 'Critical', color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
] as const;

export function FeedbackForm({
  onSubmit,
  defaultComponent,
  title = 'Submit Feedback',
  description = 'Share your ideas, report issues, or ask questions.',
  compact = false,
  noCard = false
}: FeedbackFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FeedbackFormData>({
    title: '',
    description: '',
    feedback_type: 'FEATURE_REQUEST',
    priority: 'MEDIUM',
    component: defaultComponent || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (formData.title.length < 5) {
      setErrorMessage(t('feedback.titleMinLength'));
      setSubmitStatus('error');
      return;
    }
    if (formData.description.length < 10) {
      setErrorMessage(t('feedback.descriptionMinLength'));
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await onSubmit(formData);
      setSubmitStatus('success');
      // Reset form after successful submission
      setFormData({
        title: '',
        description: '',
        feedback_type: 'FEATURE_REQUEST',
        priority: 'MEDIUM',
        component: defaultComponent || ''
      });
      // Reset success status after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error: any) {
      setSubmitStatus('error');
      setErrorMessage(error.message || t('feedback.failedToSubmit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedType = FEEDBACK_TYPES.find(t => t.value === formData.feedback_type);
  const TypeIcon = selectedType?.icon || Sparkles;

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Feedback Type Selection */}
      <div className="space-y-2">
        <Label>{t('feedback.typeLabel')}</Label>
        <div className="flex flex-wrap gap-2">
          {FEEDBACK_TYPES.map((type) => {
            const Icon = type.icon;
            const isSelected = formData.feedback_type === type.value;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, feedback_type: type.value as FeedbackType }))}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all",
                  isSelected
                    ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400"
                    : "border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 text-gray-600 dark:text-gray-400"
                )}
              >
                <Icon className={cn("w-4 h-4", isSelected ? type.color : "")} />
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="feedback-title">{t('feedback.feedbackTitle')} *</Label>
        <Input
          id="feedback-title"
          placeholder={t('feedback.titlePlaceholder')}
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          maxLength={200}
          required
          className="bg-white dark:bg-[#111]"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="feedback-description">{t('feedback.feedbackDescription')} *</Label>
        <Textarea
          id="feedback-description"
          placeholder={t('feedback.descriptionPlaceholder')}
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={compact ? 3 : 5}
          maxLength={5000}
          required
          className="bg-white dark:bg-[#111] resize-y"
        />
        <p className="text-xs text-gray-500 text-right">{formData.description.length}/5000</p>
      </div>

      {/* Priority and Component in a row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Priority */}
        <div className="space-y-2">
          <Label htmlFor="feedback-priority">{t('feedback.priorityLabel')}</Label>
          <Select
            value={formData.priority}
            onValueChange={(value: FeedbackPriority) => setFormData(prev => ({ ...prev, priority: value }))}
          >
            <SelectTrigger className="bg-white dark:bg-[#111]">
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((priority) => (
                <SelectItem key={priority.value} value={priority.value}>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn("text-xs", priority.color)}>
                      {priority.label}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Component */}
        <div className="space-y-2">
          <Label htmlFor="feedback-component">Component (optional)</Label>
          <Input
            id="feedback-component"
            placeholder="e.g., Dashboard, Crawler, Settings..."
            value={formData.component}
            onChange={(e) => setFormData(prev => ({ ...prev, component: e.target.value }))}
            maxLength={100}
            className="bg-white dark:bg-[#111]"
          />
        </div>
      </div>

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>Thank you! Your feedback has been submitted successfully.</span>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={isSubmitting || !formData.title || !formData.description}
          className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Feedback
            </>
          )}
        </Button>
      </div>
    </form>
  );

  if (noCard) {
    return formContent;
  }

  return (
    <Card className="bg-white dark:bg-[#111] border-gray-200 dark:border-white/10">
      <CardHeader className={compact ? "pb-3" : ""}>
        <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <TypeIcon className={cn("w-5 h-5", selectedType?.color || "text-fuchsia-500")} />
          {title}
        </CardTitle>
        <CardDescription className="text-gray-500 dark:text-gray-400">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className={compact ? "pt-0" : ""}>
        {formContent}
      </CardContent>
    </Card>
  );
}

export default FeedbackForm;
